<template>
  <div id="application">
    <v-app :class="{ blur: isDialogOpen }">
      <div style="position: absolute; left: 0; top: 10px; height: 10px; width: 10px" data-test-tooltip="focus-out" />
      <div style="position: absolute; width: 100%; height: 10px">
        <v-progress-linear :active="isLoading()" :indeterminate="isLoading()" color="primary" />
      </div>
      <div v-if="(isOfficesDownloaded && isAppSettingsDownloaded && appSetup) || isLoginPage()">
        <layout-header v-if="$store.direct.getters.user.isLogged" />
        <router-view :class="{ blur: isDialogOpen }" />
      </div>
      <div v-else class="ma-12 pa-12" style="text-align: center">
        <loading-in-progress />
      </div>
      <alert-popup />
      <confirm-popup />
      <prompt-popup />
      <snackbar />
      <record-stamp-dialog
        :record-stamp-id.sync="$store.direct.state.recordStamp.recordStampId"
        :title="$store.direct.state.recordStamp.title"
      />
    </v-app>
  </div>
</template>

<script lang="ts">
import "reflect-metadata";
import { Component, Vue } from "vue-property-decorator";
import LayoutHeader from "@/components/shared/layout/LayoutHeader.vue";
import store, { vuexStore } from "@/store/index";
import LoadingInProgress from "@/components/shared/LoadingInProgress.vue";
import { DEFAULT_LOGGED_ROUTE } from "@/store/modules/user";
import { CustomRoute } from "@/models/plugins/CustomRouterTypes";
import { hasPermissionForRoute } from "@/plugins/router";
import AlertPopup from "@/components/popup/AlertPopup.vue";
import ConfirmPopup from "@/components/popup/ConfirmPopup.vue";
import PromptPopup from "@/components/popup/PromptPopup.vue";
import Snackbar from "@/components/popup/Snackbar.vue";
import { getRouteWithoutCompanyFromCurrentHref } from "@/utils/getParamsFromPath";
import WaitForRequestDialog from "@/components/shared/WaitForRequestDialog.vue";
import RecordStampDialog from "@/components/recordStamp/RecordStampDialog.vue";

function registerOnPopState() {
  window.onpopstate = function () {
    vuexStore.commit.popup.CLEAR_CONFIRMS();
    vuexStore.commit.popup.CLEAR_ALERTS();
    vuexStore.commit.popup.CLEAR_PROMPTS();
  };
}

@Component({
  components: {
    WaitForRequestDialog,
    Snackbar,
    PromptPopup,
    ConfirmPopup,
    AlertPopup,
    LoadingInProgress,
    LayoutHeader,
    RecordStampDialog,
  },
})
export default class App extends Vue {
  appSetup = false;
  async created(): Promise<void> {
    await store.dispatch.downloadAppSettings();
    store.dispatch.offices.download().then(() => {
      store.dispatch.offices.redirectToDefaultOfficeIfWrongOffice();
      if (store.getters.user.isLogged) {
        store.dispatch.user.restoreToken().then(() => {
          store.dispatch.user.downloadPermissions().then(() => {
            store.dispatch.user.downloadUserInfo();
            hasPermissionForRoute(this.$router.currentRoute).then((hasPermissions) => {
              if (this.isAnonymousRoute()) {
                this.$router.push(DEFAULT_LOGGED_ROUTE);
              } else if (!hasPermissions) {
                this.$router.push({ name: "Insufficient Permissions" });
              }
              this.appSetup = true;
            });
          });
        });
      } else if (!this.isAnonymousRoute()) {
        this.$router.push({
          name: "Login",
          params: {
            redirect: encodeURIComponent(getRouteWithoutCompanyFromCurrentHref()),
          },
        });
        this.appSetup = true;
      } else {
        this.appSetup = true;
      }
    });
    registerOnPopState();
  }
  isAnonymousRoute(): boolean {
    const currentRoute: CustomRoute = this.$router.currentRoute as CustomRoute;
    return currentRoute.meta?.isAnonymousAccess === true;
  }
  isLoginPage(): boolean {
    return location.href.search("/login") !== -1;
  }
  isLoading(): boolean {
    return store.getters.isLoading;
  }
  isLogged(): boolean {
    return store.getters.user.isLogged;
  }
  get isOfficesDownloaded(): boolean {
    return store.getters.offices.isOfficesDownloaded;
  }
  get isAppSettingsDownloaded(): boolean {
    return typeof store.state.appSettings !== "undefined";
  }
  get isDialogOpen(): boolean {
    return this.$store.direct.state.dialogCount > 0;
  }
}
</script>

<style lang="scss">
// #app {
//   font-family: Avenir, Helvetica, Arial, sans-serif;
//   -webkit-font-smoothing: antialiased;
//   -moz-osx-font-smoothing: grayscale;
//   text-align: center;
//   color: #2c3e50;
// }

// #nav {
//   padding: 30px;

//   a {
//     font-weight: bold;
//     color: #2c3e50;

//     &.router-link-exact-active {
//       color: #42b983;
//     }
//   }
// }
</style>
